/* eslint-disable react-hooks/exhaustive-deps */
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import DressingRoomContext, {
  DressingRoomContextValue,
  traitsByCategoryDefaultValue,
} from "contexts/DressingRoomContext";
import { immutableReplaceAt } from "utils/arrayUtils";
import { applyTraitChanges, getTraitCategory } from "utils/traitsUtils";
import deepcopy from "deepcopy";
import { useSearchParams } from "react-router-dom";

export type DressingRoomProviderProps = {
  availableTraits?: number[];
  originalTraits: number[];
};

const DressingRoomProvider = ({
  originalTraits,
  availableTraits,
  children,
}: PropsWithChildren<DressingRoomProviderProps>) => {
  const [searchParams, updateSearchParams] = useSearchParams();

  const [state, setState] = useState(() => ({
    history: [originalTraits],
    pointer: 0,
  }));

  const currentTraits: number[] = useMemo(
    () => state.history[state.pointer],
    [state]
  );

  const changedTraits = useMemo(() => {
    if (currentTraits && originalTraits) {
      const changedTraits = currentTraits.reduce(
        (prev: number[], tokenId: number) => {
          if (!originalTraits.includes(tokenId)) {
            return [...prev, tokenId];
          }
          return prev;
        },
        []
      );

      return changedTraits;
    }

    return [];
  }, [currentTraits, originalTraits]);

  const availableTraitsByCategory = useMemo(() => {
    const result = deepcopy(traitsByCategoryDefaultValue);

    if (availableTraits) {
      availableTraits
        .sort((a, b) => a - b)
        .forEach((tokenId) => {
          const category = getTraitCategory(tokenId);
          result[category].push(tokenId);
        });
      return result;
    }

    return result;
  }, [availableTraits, originalTraits]);

  const undoSwap = useCallback(() => {
    if (state.pointer > 0) {
      setState({ ...state, pointer: state.pointer - 1 });
    }
  }, [state]);

  const redoSwap = useCallback(() => {
    if (state.pointer + 1 < state.history.length) {
      setState({ ...state, pointer: state.pointer + 1 });
    }
  }, [state]);

  const resetTraits = useCallback(() => {
    setState({ history: [originalTraits], pointer: 0 });
  }, [originalTraits]);

  const swapTrait = useCallback(
    (newTraits: number[]) => {
      const newSwaps = [
        ...state.history.slice(0, state.pointer + 1),
        newTraits,
      ];
      setState({ history: newSwaps, pointer: state.pointer + 1 });
    },
    [state]
  );

  const removeTrait = useCallback(
    (traitId: number) => {
      const traitIndex = currentTraits.indexOf(traitId);
      if (traitIndex > -1) {
        swapTrait(
          immutableReplaceAt(
            currentTraits,
            traitIndex,
            originalTraits[traitIndex]
          )
        );
      }
    },
    [currentTraits, originalTraits, swapTrait]
  );

  useEffect(() => {
    const searchTraits = searchParams
      .get("traits")
      ?.split(",")
      ?.map((trait) => parseInt(trait))
      ?.filter(
        (trait) => !Number.isNaN(trait) && availableTraits?.includes(trait)
      );

    if (searchTraits && searchTraits.length > 0) {
      setState({
        history: [
          originalTraits,
          applyTraitChanges(originalTraits, searchTraits),
        ],
        pointer: 1,
      });
    } else {
      setState({
        history: [originalTraits],
        pointer: 0,
      });
    }
  }, [originalTraits]);

  useEffect(() => {
    if (originalTraits.length > 0) {
      updateSearchParams(
        changedTraits.length > 0
          ? {
              traits: changedTraits.join(","),
            }
          : {},
        { replace: true }
      );
    }
  }, [changedTraits]);

  const contextValue = useMemo<DressingRoomContextValue>(
    () => ({
      currentTraits,
      changedTraits,
      originalTraits,
      availableTraits,
      availableTraitsByCategory,
      canRedo: state.pointer < state.history.length - 1,
      canUndo: state.pointer > 0,
      undoSwap,
      redoSwap,
      resetTraits,
      swapTrait,
      removeTrait,
    }),
    [
      state,
      undoSwap,
      redoSwap,
      resetTraits,
      swapTrait,
      originalTraits,
      availableTraits,
      availableTraitsByCategory,
      changedTraits,
      currentTraits,
      removeTrait,
    ]
  );

  return (
    <DressingRoomContext.Provider value={contextValue}>
      {children}
    </DressingRoomContext.Provider>
  );
};

export default DressingRoomProvider;
